import React from 'react';
import {
  IContactHubProps,
  contactType,
  supportItemType,
} from './ContactHubTypes';
import {
  Grid,
  Heading,
  Link,
  TextBoldness,
  Typography,
  getClassNameFactory,
  getRehydratableName,
} from '@dcl/ui';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import {
  ContactHubEmailSupport,
  ContactHubPhoneSupport,
} from '@digital-csp/help-center-shared';
import ContactHubModal from './components/ContactHubModal/ContactHubModal';
import { LinkTypes } from '@dcl/ui/dist/elements/Link';
import LinkIcon from '../FileDownloadExperience/icons/LinkIcon';
import './index.css';

export const ContactHub: React.FC<IContactHubProps> = (props) => {
  const { contactUsTitle, communicationList } = props;
  const getClassName = getClassNameFactory(ContactHub.displayName);

  // Helper function to filter supportHoursListSingle array
  const filterDirectLinks = (supportHoursListSingle) => {
    if (!supportHoursListSingle || !Array.isArray(supportHoursListSingle)) {
      return [];
    }

    // Only keep items that have buttonLabel and buttonLink but no supportLinkList
    return supportHoursListSingle.filter(
      (item) => item.buttonLabel && item.buttonLink && !item.supportLinkList
    );
  };

  const getContactSupport = (item) => {
    switch (item.type) {
      case supportItemType.phoneSingle: {
        // Filter the supportHoursListSingle array to only include direct links
        const directLinks = filterDirectLinks(item.supportHoursListSingle);

        return (
          <ContactHubPhoneSupport
            phoneNumber={item.phoneNumber}
            phoneText={item.phoneText}
            timings={item.timings}
            supportHoursListSingle={directLinks}
          />
        );
      }
      case supportItemType.emailSingle:
        return (
          <ContactHubEmailSupport
            emailId={item.email}
            emailDisplayText={item.emailLabel}
          />
        );
      case supportItemType.phoneMulti:
        return (
          <ContactHubModal
            openModalButtonText={item.buttonText}
            modalTitle={item.supportTitle}
            modalSubTitle={item.supportSubLabel}
            dropdownLabel={item.supportLabel}
            dropdownItems={item.phoneSupportList}
            modalCloseButtonLabel={item.closeLabel}
            type={contactType.phone}
          />
        );
      case supportItemType.phoneWithCountry:
        return (
          <ContactHubModal
            openModalButtonText={item.phoneButtonText}
            modalTitle={item.countryTitle}
            modalSubTitle={item.countrySubLabel}
            dropdownLabel={item.countryLabel}
            dropdownItems={item.countryPhoneList}
            modalCloseButtonLabel={item.phoneCloseLabel}
            type={contactType.phone}
          />
        );
      case supportItemType.emailMutli:
        return (
          <ContactHubModal
            type={contactType.email}
            openModalButtonText={item.emailButtonText}
            modalCloseButtonLabel={item.emailCloseLabel}
            modalTitle={item.emailTitle}
            emailSupportItems={item.emailList}
          />
        );

      case supportItemType.manage:
        return (
          <div className={getClassName({ descendantName: 'manageLink' })}>
            {item.supportLinkList?.map((link) => (
              <Link
                key={link.buttonLink}
                href={link.buttonLink}
                data-testid={'manageLink'}
                type={LinkTypes.INLINE}
                newWindow={link.newTab}
                iconRight={<LinkIcon />}
              >
                {link.buttonLabel}
              </Link>
            ))}
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div
      className={getClassName({ descendantName: 'container' })}
      data-rehydratable={getRehydratableName(ContactHub.displayName)}
      {...propsToDataAttributes(props)}
    >
      <Heading
        className={getClassName({ descendantName: 'title' })}
        type={types.Medium}
        boldType={boldTypes.medium}
      >
        {contactUsTitle}
      </Heading>
      <Grid variant="even-3">
        {communicationList?.map((communication, index) => {
          const keyValue = `${communication.title}-${index + 1}`;
          return (
            <Grid.Item key={keyValue}>
              <div
                className={getClassName({ descendantName: 'contactUsCard' })}
              >
                <Heading
                  className={getClassName({
                    descendantName: 'contactUsCardHeader',
                  })}
                  type={types.Small}
                  boldType={boldTypes.medium}
                >
                  {communication.title}
                </Heading>
                <Typography
                  tag={TypographyTags.SPAN}
                  boldness={TextBoldness.Regular}
                  className={getClassName({
                    descendantName: 'description',
                  })}
                >
                  {communication.description}
                </Typography>
                {getContactSupport(communication)}
              </div>
            </Grid.Item>
          );
        })}
      </Grid>
    </div>
  );
};

ContactHub.displayName = 'ContactHub';
